import { formatUnsafeMoney } from '@/app/document-review/unsafe-money'
import type { DocumentReviewDetailOutput, DocumentReviewProposalView } from '@/app/document-review/use-document-review'

export type FiledRecord = {
  proposal: DocumentReviewProposalView
  joined: boolean
  amount: string | null
} & ({ orderEventId: number; paymentId: null } | { orderEventId: null; paymentId: number })

export type DuplicateRecord = {
  proposal: DocumentReviewProposalView
  orderEventId: number
}

export function duplicateRecords(detail: DocumentReviewDetailOutput): DuplicateRecord[] {
  return (detail.execution?.duplicates ?? []).flatMap(({ ordinal, orderEventId }) => {
    const proposal = detail.proposals.find((one) => one.ordinal === ordinal)
    return proposal === undefined ? [] : [{ proposal, orderEventId }]
  })
}

export function filedOrderEventId(proposal: DocumentReviewProposalView): number | null {
  if (proposal.orderEventId !== null) return proposal.orderEventId
  const record = proposal.record
  if (record === null || record.kind !== 'attach' || proposal.orderEventDocumentId === null) return null
  return record.orderEventId
}

export function filedRecordMade(proposal: DocumentReviewProposalView): boolean {
  return proposal.paymentId !== null || filedOrderEventId(proposal) !== null
}

export function joinsFinalizedRecord(proposal: DocumentReviewProposalView): boolean {
  if (proposal.record?.kind !== 'attach') return false
  if (filedOrderEventId(proposal) === null) return proposal.recordFinalization !== null
  return proposal.doubts.some((doubt) => doubt.kind === 'finalized_record')
}

export function filedAsUpload(detail: DocumentReviewDetailOutput): boolean {
  return detail.proposals.length > 0 && detail.proposals.every((proposal) => proposal.kind === 'trader_document')
}

export function joinsAsDuplicate(proposal: DocumentReviewProposalView): boolean {
  return proposal.record?.kind === 'attach' && proposal.doubts.some((doubt) => doubt.kind === 'same_file_on_record')
}

function figureOf(proposal: DocumentReviewProposalView): string | null {
  const record = proposal.record
  if (record === null || record.kind === 'attach_payment' || joinsFinalizedRecord(proposal)) return null
  if (record.kind !== 'attach') return record.fields.amount
  return record.updates?.amount ?? record.contribution?.amount ?? null
}

export function filedRecords(detail: DocumentReviewDetailOutput): FiledRecord[] {
  return detail.proposals.flatMap((proposal): FiledRecord[] => {
    const figure = figureOf(proposal)
    const amount = figure === null ? null : (formatUnsafeMoney(figure) ?? figure)
    if (proposal.paymentId !== null) {
      const joined = proposal.record?.kind === 'attach_payment'
      return [{ proposal, orderEventId: null, paymentId: proposal.paymentId, joined, amount }]
    }
    const orderEventId = filedOrderEventId(proposal)
    if (orderEventId === null) return []
    const record = proposal.record
    return [{ proposal, orderEventId, paymentId: null, joined: record !== null && record.kind === 'attach', amount }]
  })
}
